/**
 * Formulaire d'édition d'un jeu de règles.
 *
 * Il travaille sur une copie locale et remonte chaque changement au parent :
 * c'est le parent qui décide quand enregistrer. Une règle modifiée n'est donc
 * jamais écrite en base par accident — l'écran affiche « non enregistré » tant
 * que l'utilisateur n'a pas validé.
 */

import type { ChannelPricing, MarginTier, OutputRules, PreparationRules, RuleSet } from '@/api/types'
import { Card, CardBody, CardHead, CardNote, Checkbox, Field, Grid } from '@/components/ui'
import {
  FILE_LABELS,
  PREVIEW_PRODUCT_NAME,
  PRICING_CHANNELS,
  PRICING_CHANNEL_LABELS,
  addMarginTier,
  describeTier,
  normalizeMargins,
  removeMarginTier,
  renderMetaDescription,
} from '@/lib/rules'
import { Button, Message } from '@/components/ui'
import styles from '@/pages/RulesPage.module.css'

interface Props {
  rules: RuleSet
  onChange: (next: RuleSet) => void
}

export function RulesForm({ rules, onChange }: Props) {
  function patchOutput<K extends keyof OutputRules>(key: K, value: OutputRules[K]) {
    onChange({ ...rules, output: { ...rules.output, [key]: value } })
  }

  return (
    <>
      <GeneralCard rules={rules} onChange={onChange} />
      <PreparationCard
        preparation={rules.preparation}
        onChange={(preparation) => onChange({ ...rules, preparation })}
      />
      <PricingCard rules={rules} onChange={onChange} />
      <AmazonCard output={rules.output} patch={patchOutput} />
      <MiraklCard output={rules.output} patch={patchOutput} />
      <DartyCard output={rules.output} patch={patchOutput} />
      <YoukapiCard output={rules.output} patch={patchOutput} />
    </>
  )
}

// --- Paramètres généraux -----------------------------------------------------

function GeneralCard({ rules, onChange }: Props) {
  return (
    <Card>
      <CardHead title="Paramètres généraux" />
      <CardBody>
        <CardNote>
          Tout se décide sur la colonne « Stock » du catalogue, ligne par ligne : un article
          à réapprovisionner n’impose pas son délai aux autres. En stock, c’est le stock réel
          qui est publié, sous le plafond ci-dessous, avec le délai court. Hors stock — zéro,
          négatif ou case vide — la ligne part quand même au plafond, les places de marché
          refusant zéro, mais avec le délai long : c’est lui, et non la quantité, qui dit que
          l’article est à commander.
        </CardNote>
        <Grid columns={4}>
          <Field
            label="Délai en stock (jours)"
            help="Article disponible : stock ≥ 1."
            htmlFor="handling"
          >
            <input
              id="handling"
              type="number"
              min={0}
              value={rules.output.handling_time_days}
              onChange={(event) =>
                onChange({
                  ...rules,
                  output: { ...rules.output, handling_time_days: Number(event.target.value) || 0 },
                })
              }
            />
          </Field>
          <Field
            label="Délai hors stock (jours)"
            help="Article à réapprovisionner : stock < 1."
            htmlFor="handlingOut"
          >
            <input
              id="handlingOut"
              type="number"
              min={0}
              value={rules.output.handling_time_out_of_stock_days}
              onChange={(event) =>
                onChange({
                  ...rules,
                  output: {
                    ...rules.output,
                    handling_time_out_of_stock_days: Number(event.target.value) || 0,
                  },
                })
              }
            />
          </Field>
          <Field label="Seuil colis lourd (kg)" help="Au-delà, Amazon passe au barème 7 %." htmlFor="threshold">
            <input
              id="threshold"
              type="number"
              step={0.5}
              min={0}
              value={rules.pricing.heavy_threshold_kg}
              onChange={(event) =>
                onChange({
                  ...rules,
                  pricing: { ...rules.pricing, heavy_threshold_kg: Number(event.target.value) || 0 },
                })
              }
            />
          </Field>
          <Field
            label="Quantité publiée au plus"
            help="Au-delà, le stock est ramené ici — et un article hors stock y part aussi."
            htmlFor="maxQuantity"
          >
            <input
              id="maxQuantity"
              type="number"
              step={1}
              min={1}
              value={rules.output.max_published_quantity}
              onChange={(event) =>
                onChange({
                  ...rules,
                  output: {
                    ...rules.output,
                    max_published_quantity: Number(event.target.value) || 1,
                  },
                })
              }
            />
          </Field>
        </Grid>

        <div style={{ marginTop: 18 }}>
          <label>Fichiers générés par défaut</label>
          <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 8 }}>
            {(['amazon', 'mirakl', 'darty', 'youkapi'] as const).map((channel) => {
              const checked = rules.channels.includes(channel)
              const last = checked && rules.channels.length === 1
              return (
                <Checkbox
                  key={channel}
                  checked={checked}
                  disabled={last}
                  label={FILE_LABELS[channel].name}
                  onChange={(next) =>
                    onChange({
                      ...rules,
                      channels: next
                        ? [...rules.channels, channel]
                        : rules.channels.filter((item) => item !== channel),
                    })
                  }
                />
              )
            })}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

// --- Préparation du catalogue fournisseur ------------------------------------

/**
 * Ce qui transforme l'export brut du fournisseur en catalogue d'entrée.
 *
 * Ces valeurs bougent plus souvent que les barèmes : une marque devient plus
 * chère qu'ailleurs, un fournisseur ajoute une catégorie. Elles se règlent donc
 * ici, et chaque enregistrement crée une version comme le reste.
 */
function PreparationCard({
  preparation,
  onChange,
}: {
  preparation: PreparationRules
  onChange: (next: PreparationRules) => void
}) {
  function patch<K extends keyof PreparationRules>(key: K, value: PreparationRules[K]) {
    onChange({ ...preparation, [key]: value })
  }

  return (
    <Card>
      <CardHead
        title="Préparation du catalogue fournisseur"
        actions={
          <Checkbox
            checked={preparation.enabled}
            onChange={(next) => patch('enabled', next)}
            label="Activée"
          />
        }
      />
      <CardBody>
        <CardNote>
          S’applique au seul export brut du fournisseur, reconnu à ses colonnes « Pnb » et
          « Stock ». Un catalogue déjà préparé, ou venu d’ailleurs, passe sans être touché.
        </CardNote>

        <Grid columns={3}>
          <Field label="Prix d’achat minimum (€)" help="En dessous, la ligne est écartée.">
            <input
              type="number"
              step={1}
              min={0}
              disabled={!preparation.enabled}
              value={preparation.min_price}
              onChange={(event) => patch('min_price', Number(event.target.value) || 0)}
            />
          </Field>
          <Field label="Prix d’achat maximum (€)">
            <input
              type="number"
              step={1}
              min={0}
              disabled={!preparation.enabled}
              value={preparation.max_price}
              onChange={(event) => patch('max_price', Number(event.target.value) || 0)}
            />
          </Field>
          <Field
            label="Stock minimum"
            help="Vide : aucun filtre. Le stock « -1 » passe, et part au délai hors stock."
          >
            <input
              type="number"
              step={1}
              disabled={!preparation.enabled}
              value={preparation.min_quantity ?? ''}
              onChange={(event) =>
                patch('min_quantity', event.target.value === '' ? null : Number(event.target.value))
              }
            />
          </Field>
        </Grid>

        <Grid columns={2}>
          <Field
            label="Marques écartées"
            help="Séparées par des virgules. Celles qu’on trouve moins cher ailleurs."
          >
            <input
              type="text"
              disabled={!preparation.enabled}
              value={preparation.excluded_brands.join(', ')}
              onChange={(event) => patch('excluded_brands', splitList(event.target.value))}
            />
          </Field>
          <Field
            label="Poids manquant (kg)"
            help="Lignes dont le fournisseur (VDB ou SOM) ne publie aucun poids."
          >
            <input
              type="number"
              step={0.01}
              min={0.1}
              disabled={!preparation.enabled}
              value={preparation.missing_weight}
              onChange={(event) => patch('missing_weight', Number(event.target.value) || 1)}
            />
          </Field>
        </Grid>

        <Grid columns={2}>
          <Field
            label="Préfixes « frigo américain »"
            help="Séparés par des virgules. Vide : la règle ne s’applique pas."
          >
            <input
              type="text"
              disabled={!preparation.enabled}
              value={preparation.oversize_prefixes.join(', ')}
              onChange={(event) => patch('oversize_prefixes', splitList(event.target.value))}
            />
          </Field>
          <Field label="Poids de ces articles (kg)">
            <input
              type="number"
              step={1}
              min={0.1}
              disabled={!preparation.enabled}
              value={preparation.oversize_weight}
              onChange={(event) => patch('oversize_weight', Number(event.target.value) || 1)}
            />
          </Field>
        </Grid>

        <Message kind={preparation.oversize_prefixes.length ? 'info' : 'ok'}>
          {preparation.oversize_prefixes.length ? (
            <>
              <b>Règle active.</b> À défaut de poids publié, le préfixe ne regarde pas ce
              qu’est l’article : une référence « RF… » ou « KA… » prend ce poids même si c’est un
              congélateur ou un chauffe-lit. L’écran de conversion liste les articles concernés à
              chaque préparation — vérifiez-les avant d’envoyer.
            </>
          ) : (
            <>
              <b>Règle en veille.</b> Le poids vient du fichier fournisseur. Pour la rétablir sans
              attraper les congélateurs « RFSA… » ni les chauffe-lits « KA5… », visez plus long :
              <code> RF2, RF5, RF6, KAD, KA9</code>.
            </>
          )}
        </Message>

        <Grid columns={3}>
          <Field
            label="Préfixes « grande hauteur »"
            help="Séparés par des virgules. Vide : la règle ne s’applique pas."
          >
            <input
              type="text"
              disabled={!preparation.enabled}
              value={preparation.tall_prefixes.join(', ')}
              onChange={(event) => patch('tall_prefixes', splitList(event.target.value))}
            />
          </Field>
          <Field
            label="Hauteur au-delà de (cm)"
            help="Hauteur publiée par le fournisseur."
          >
            <input
              type="number"
              step={1}
              min={1}
              disabled={!preparation.enabled}
              value={preparation.tall_height}
              onChange={(event) => patch('tall_height', Number(event.target.value) || 192)}
            />
          </Field>
          <Field label="Poids de ces articles (kg)">
            <input
              type="number"
              step={1}
              min={0.1}
              disabled={!preparation.enabled}
              value={preparation.tall_weight}
              onChange={(event) => patch('tall_weight', Number(event.target.value) || 1)}
            />
          </Field>
        </Grid>

        <Message kind="info">
          <b>Double contrôle.</b> Un modèle de plus de{' '}
          {String(preparation.tall_height).replace('.', ',')} cm
          {preparation.tall_prefixes.length ? ', ou dont la référence commence par un préfixe déclaré,' : ''}{' '}
          pèse {String(preparation.tall_weight).replace('.', ',')} kg — quel que soit le poids
          publié par VDB ou SOM, et même quand le prix SOM l’emporte.
          {preparation.tall_prefixes.length
            ? ' Le préfixe ne lit que le début de la référence : l’écran de conversion liste les articles touchés à chaque préparation.'
            : ' Aucun préfixe déclaré : seule la hauteur publiée joue. Visez des préfixes assez longs pour ne désigner que ces modèles.'}
        </Message>
      </CardBody>
    </Card>
  )
}

/** « BOSCH, SIEMENS » → `['BOSCH', 'SIEMENS']`, sans entrée vide. */
function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

// --- Barèmes de prix ---------------------------------------------------------

function PricingCard({ rules, onChange }: Props) {
  function patchChannel(key: (typeof PRICING_CHANNELS)[number], patch: Partial<ChannelPricing>) {
    onChange({
      ...rules,
      pricing: { ...rules.pricing, [key]: { ...rules.pricing[key], ...patch } },
    })
  }

  return (
    <Card>
      <CardHead title="Barèmes de prix" />
      <CardBody>
        <CardNote>
          Prix de vente = (prix d’achat + marge + transport) × TVA × commission, plus la TVA récupérée sur la
          commission. Les tranches de marge se lisent sur le prix d’achat.
        </CardNote>

        {PRICING_CHANNELS.map((key) => {
          const channel = rules.pricing[key]
          const label = PRICING_CHANNEL_LABELS[key]
          return (
            <div key={key} className={styles.channel}>
              <h4>
                <i className={styles.swatch} style={{ background: label.color }} aria-hidden />
                {label.label}
              </h4>
              <p className={styles.channelHint}>{label.hint}</p>

              <Grid columns={4}>
                <Field label="TVA (×)" help="1,21 = 21 %">
                  <input
                    type="number"
                    step={0.0001}
                    min={0}
                    value={channel.vat}
                    onChange={(event) => patchChannel(key, { vat: Number(event.target.value) || 0 })}
                  />
                </Field>
                <Field label="Commission (×)" help="1,0721 = 7,21 %">
                  <input
                    type="number"
                    step={0.0001}
                    min={0}
                    value={channel.commission}
                    onChange={(event) => patchChannel(key, { commission: Number(event.target.value) || 0 })}
                  />
                </Field>
                <Field label="Transport fixe (€)">
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    value={channel.shipping_flat}
                    onChange={(event) => patchChannel(key, { shipping_flat: Number(event.target.value) || 0 })}
                  />
                </Field>
                <Field label="Transport (€/kg)">
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    value={channel.shipping_per_kg}
                    onChange={(event) => patchChannel(key, { shipping_per_kg: Number(event.target.value) || 0 })}
                  />
                </Field>
              </Grid>

              <Grid columns={4}>
                <Field label="TVA récupérée sur commission" help="0,20 = 20 %, 0 = aucune">
                  <input
                    type="number"
                    step={0.01}
                    min={0}
                    max={1}
                    value={channel.vat_on_commission}
                    onChange={(event) =>
                      patchChannel(key, { vat_on_commission: Number(event.target.value) || 0 })
                    }
                  />
                </Field>
                <div className={styles.checkStack} style={{ gridColumn: 'span 3' }}>
                  <Checkbox
                    checked={channel.round_to_9}
                    onChange={(next) => patchChannel(key, { round_to_9: next })}
                    label={<>Arrondi commercial en …9&nbsp;<span style={{ color: 'var(--text-3)', fontSize: 12 }}>(499, 509, 519…)</span></>}
                  />
                </div>
              </Grid>

              <MarginTiers
                tiers={channel.margins}
                onChange={(margins) => patchChannel(key, { margins })}
              />
            </div>
          )
        })}
      </CardBody>
    </Card>
  )
}

function MarginTiers({ tiers, onChange }: { tiers: MarginTier[]; onChange: (next: MarginTier[]) => void }) {
  function patchTier(index: number, patch: Partial<MarginTier>) {
    onChange(tiers.map((tier, position) => (position === index ? { ...tier, ...patch } : tier)))
  }

  return (
    <>
      <div className={styles.tiersHead}>
        <span>
          Marge par tranche de prix d’achat <em>— la dernière tranche couvre tout le reste</em>
        </span>
        <Button variant="secondary" small onClick={() => onChange(addMarginTier(tiers))}>
          Ajouter une tranche
        </Button>
      </div>

      <div className={styles.tiers}>
        <div className={`${styles.tierRow} ${styles.tierHeader}`}>
          <span>Prix d’achat inférieur à (€)</span>
          <span>Marge (€)</span>
          <span>Tranche</span>
          <span />
        </div>

        {tiers.map((tier, index) => {
          const open = index === tiers.length - 1
          return (
            <div key={index} className={styles.tierRow}>
              {open ? (
                <div className={styles.openTier}>tout le reste</div>
              ) : (
                <input
                  type="number"
                  step={1}
                  min={0}
                  aria-label={`Borne de la tranche ${index + 1}`}
                  value={tier.upper ?? ''}
                  onChange={(event) =>
                    patchTier(index, { upper: event.target.value === '' ? null : Number(event.target.value) })
                  }
                  onBlur={() => onChange(normalizeMargins(tiers))}
                />
              )}
              <input
                type="number"
                step={1}
                aria-label={`Marge de la tranche ${index + 1}`}
                value={tier.margin}
                onChange={(event) => patchTier(index, { margin: Number(event.target.value) || 0 })}
              />
              <span className={styles.tierLabel}>{describeTier(tiers, index)}</span>
              <button
                type="button"
                className={styles.removeTier}
                title="Supprimer la tranche"
                aria-label={`Supprimer la tranche ${index + 1}`}
                disabled={tiers.length < 2}
                onClick={() => onChange(removeMarginTier(tiers, index))}
              >
                ×
              </button>
            </div>
          )
        })}
      </div>
    </>
  )
}

// --- Amazon ------------------------------------------------------------------

type Patch = <K extends keyof OutputRules>(key: K, value: OutputRules[K]) => void

function AmazonCard({ output, patch }: { output: OutputRules; patch: Patch }) {
  return (
    <Card>
      <CardHead title="Amazon — valeurs fixes du template" />
      <CardBody>
        <Grid columns={2}>
          <Field label="Action sur la fiche">
            <input
              type="text"
              value={output.amazon_record_action}
              onChange={(event) => patch('amazon_record_action', event.target.value)}
            />
          </Field>
          <Field label="Type d'identifiant produit">
            <input
              type="text"
              value={output.amazon_product_id_type}
              onChange={(event) => patch('amazon_product_id_type', event.target.value)}
            />
          </Field>
          <Field label="État du produit">
            <input
              type="text"
              value={output.amazon_condition}
              onChange={(event) => patch('amazon_condition', event.target.value)}
            />
          </Field>
          <Field label="Canal d'expédition">
            <input
              type="text"
              value={output.amazon_fulfillment_channel}
              onChange={(event) => patch('amazon_fulfillment_channel', event.target.value)}
            />
          </Field>
          <Field label="Modèle d'expédition" help="Nom du modèle défini dans Seller Central.">
            <input
              type="text"
              value={output.amazon_shipping_group}
              onChange={(event) => patch('amazon_shipping_group', event.target.value)}
            />
          </Field>
        </Grid>
      </CardBody>
    </Card>
  )
}

// --- Mirakl ------------------------------------------------------------------

function MiraklCard({ output, patch }: { output: OutputRules; patch: Patch }) {
  return (
    <Card>
      <CardHead title="Mirakl — valeurs fixes de l'offre" />
      <CardBody>
        <CardNote>
          Mêmes colonnes, dans le même ordre, que le fichier Amazon envoyé jusqu’ici à Mirakl, mais dans un
          classeur .xlsx : le back-office refuse désormais le .xlsm. Le prix publié est celui du canal Amazon.
        </CardNote>
        <Grid columns={4}>
          <Field label="Type d'identifiant produit" help="4 = EAN.">
            <input
              type="text"
              value={output.mirakl_product_id_type}
              onChange={(event) => patch('mirakl_product_id_type', event.target.value)}
            />
          </Field>
          <Field label="État du produit" help="11 = neuf.">
            <input
              type="number"
              value={output.mirakl_condition}
              onChange={(event) => patch('mirakl_condition', Number(event.target.value) || 0)}
            />
          </Field>
          <Field label="Action sur l'offre" help="« a » = ajout ou mise à jour.">
            <input
              type="text"
              value={output.mirakl_add_delete}
              onChange={(event) => patch('mirakl_add_delete', event.target.value)}
            />
          </Field>
          <Field label="Modèle d'expédition">
            <input
              type="text"
              value={output.mirakl_shipping_group}
              onChange={(event) => patch('mirakl_shipping_group', event.target.value)}
            />
          </Field>
        </Grid>
      </CardBody>
    </Card>
  )
}

// --- Darty -------------------------------------------------------------------

function DartyCard({ output, patch }: { output: OutputRules; patch: Patch }) {
  return (
    <>
      <Card>
        <CardHead title="Darty — valeurs fixes de l'offre" />
        <CardBody>
          <Grid columns={4}>
            <Field label="État du produit" help="11 = neuf.">
              <input
                type="number"
                value={output.darty_condition}
                onChange={(event) => patch('darty_condition', Number(event.target.value) || 0)}
              />
            </Field>
            <Field label="Type de produit" help="4 = EAN.">
              <input
                type="number"
                value={output.darty_type_id_produit}
                onChange={(event) => patch('darty_type_id_produit', Number(event.target.value) || 0)}
              />
            </Field>
            <Field label="Famille logistique" help="107 = volumineux G.">
              <input
                type="number"
                value={output.darty_logistic_type_id}
                onChange={(event) => patch('darty_logistic_type_id', Number(event.target.value) || 0)}
              />
            </Field>
            <div className={styles.checkStack}>
              <Checkbox
                checked={output.darty_is_shipping_free === 1}
                onChange={(next) => patch('darty_is_shipping_free', next ? 1 : 0)}
                label="Livraison gratuite"
              />
            </div>
          </Grid>
          <Grid columns={2}>
            <Field label="Informations de garantie">
              <input
                type="text"
                value={output.darty_warranty}
                onChange={(event) => patch('darty_warranty', event.target.value)}
              />
            </Field>
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Darty — format du fichier CSV" />
        <CardBody>
          <CardNote>
            Le back-office Darty n’accepte que du CSV : il refuse le classeur, y compris son propre modèle
            rempli. Ne changez ces valeurs que si la notice du modèle évolue.
          </CardNote>
          <Grid columns={3}>
            <Field label="Séparateur de colonnes">
              <select
                value={output.darty_csv_separator}
                onChange={(event) =>
                  patch('darty_csv_separator', event.target.value as OutputRules['darty_csv_separator'])
                }
              >
                <option value=";">Point-virgule ( ; )</option>
                <option value=",">Virgule ( , )</option>
                <option value={'\t'}>Tabulation</option>
                <option value="|">Barre verticale ( | )</option>
              </select>
            </Field>
            <Field label="Séparateur décimal">
              <select
                value={output.darty_decimal_separator}
                onChange={(event) =>
                  patch('darty_decimal_separator', event.target.value as OutputRules['darty_decimal_separator'])
                }
              >
                <option value=",">Virgule ( , )</option>
                <option value=".">Point ( . )</option>
              </select>
            </Field>
            <Field label="Encodage">
              <select
                value={output.darty_csv_encoding}
                onChange={(event) =>
                  patch('darty_csv_encoding', event.target.value as OutputRules['darty_csv_encoding'])
                }
              >
                <option value="utf-8">UTF-8</option>
                <option value="utf-8-sig">UTF-8 avec BOM</option>
                <option value="cp1252">Windows-1252</option>
                <option value="iso-8859-1">ISO-8859-1</option>
              </select>
            </Field>
          </Grid>
        </CardBody>
      </Card>
    </>
  )
}

// --- Youkapi.com -------------------------------------------------------------

function YoukapiCard({ output, patch }: { output: OutputRules; patch: Patch }) {
  const preview = renderMetaDescription(
    output.youkapi_meta_description_template,
    PREVIEW_PRODUCT_NAME,
    output.youkapi_meta_suffix,
  )

  return (
    <>
      <Card>
        <CardHead title="Youkapi.com — fiche produit PrestaShop" />
        <CardBody>
          <Grid columns={3}>
            <Field label="Catégorie" help="Écrite telle quelle sur chaque ligne du fichier.">
              <input
                type="number"
                min={0}
                value={output.youkapi_category_id}
                onChange={(event) => patch('youkapi_category_id', Number(event.target.value) || 0)}
              />
            </Field>
            <Field label="Tax rules ID">
              <input
                type="number"
                value={output.youkapi_tax_rules_id}
                onChange={(event) => patch('youkapi_tax_rules_id', Number(event.target.value) || 0)}
              />
            </Field>
            <Field label="Visibilité">
              <select
                value={output.youkapi_visibility}
                onChange={(event) =>
                  patch('youkapi_visibility', event.target.value as OutputRules['youkapi_visibility'])
                }
              >
                <option value="both">Catalogue et recherche</option>
                <option value="catalog">Catalogue seulement</option>
                <option value="search">Recherche seulement</option>
                <option value="none">Nulle part</option>
              </select>
            </Field>
          </Grid>
          <Grid columns={3}>
            <Field label="Quantité publiée" help="Le classeur Excel écrivait une quantité figée.">
              <select
                value={output.youkapi_quantity_mode}
                onChange={(event) =>
                  patch('youkapi_quantity_mode', event.target.value as OutputRules['youkapi_quantity_mode'])
                }
              >
                <option value="catalog">Quantité réelle du catalogue</option>
                <option value="fixed">Quantité figée</option>
              </select>
            </Field>
            <Field label="Quantité figée">
              <input
                type="number"
                min={0}
                disabled={output.youkapi_quantity_mode !== 'fixed'}
                value={output.youkapi_fixed_quantity}
                onChange={(event) => patch('youkapi_fixed_quantity', Number(event.target.value) || 0)}
              />
            </Field>
            <div className={styles.checkStack}>
              <Checkbox
                checked={output.youkapi_show_price === 1}
                onChange={(next) => patch('youkapi_show_price', next ? 1 : 0)}
                label="Afficher le prix"
              />
            </div>
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Youkapi.com — images" />
        <CardBody>
          <CardNote>
            PrestaShop ajoute la photo à la fiche chaque fois qu'il en lit le lien. Sur une simple
            mise à jour de prix, qui repasse sur des fiches déjà en ligne, la même photo se
            retrouverait deux fois sur la page : répondre « Non » laisse alors la colonne vide et
            les fiches intactes.
          </CardNote>
          <Grid columns={3}>
            <Field label="Lien photo" help="Colonne « ImageURLs » du fichier converti.">
              <select
                value={output.youkapi_include_image ? 'oui' : 'non'}
                onChange={(event) => patch('youkapi_include_image', event.target.value === 'oui')}
              >
                <option value="oui">Oui — écrire le lien</option>
                <option value="non">Non — laisser la colonne vide</option>
              </select>
            </Field>
            <Field label="Source de l'URL">
              <select
                disabled={!output.youkapi_include_image}
                value={output.youkapi_image_source}
                onChange={(event) =>
                  patch('youkapi_image_source', event.target.value as OutputRules['youkapi_image_source'])
                }
              >
                <option value="template">Reconstruire (base + modèle + extension)</option>
                <option value="catalog">Colonne Image du catalogue</option>
              </select>
            </Field>
            <div />
            <Field label="URL de base">
              <input
                type="text"
                disabled={!output.youkapi_include_image || output.youkapi_image_source === 'catalog'}
                value={output.youkapi_image_base_url}
                onChange={(event) => patch('youkapi_image_base_url', event.target.value)}
              />
            </Field>
            <Field label="Extension">
              <input
                type="text"
                disabled={!output.youkapi_include_image || output.youkapi_image_source === 'catalog'}
                value={output.youkapi_image_extension}
                onChange={(event) => patch('youkapi_image_extension', event.target.value)}
              />
            </Field>
          </Grid>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="Youkapi.com — titres et descriptions" />
        <CardBody>
          <Grid columns={2}>
            <Field label="Préfixe du titre" help="Meta title = préfixe + nom du produit + suffixe.">
              <input
                type="text"
                value={output.youkapi_meta_prefix}
                onChange={(event) => patch('youkapi_meta_prefix', event.target.value)}
              />
            </Field>
            <Field label="Suffixe du titre">
              <input
                type="text"
                value={output.youkapi_meta_suffix}
                onChange={(event) => patch('youkapi_meta_suffix', event.target.value)}
              />
            </Field>
          </Grid>
          <div style={{ marginTop: 14 }}>
            <Field
              label="Modèle de meta description"
              help={
                <>
                  Deux variables disponibles : <code>{'{name}'}</code> (nom du produit) et{' '}
                  <code>{'{suffix}'}</code>.
                </>
              }
            >
              <textarea
                value={output.youkapi_meta_description_template}
                onChange={(event) => patch('youkapi_meta_description_template', event.target.value)}
              />
            </Field>
          </div>
          <Message kind="info">
            <b>Aperçu</b>
            <br />
            Titre : {output.youkapi_meta_prefix} {PREVIEW_PRODUCT_NAME} {output.youkapi_meta_suffix}
            <br />
            Description : {preview}
          </Message>
        </CardBody>
      </Card>
    </>
  )
}
